import { useEffect, useMemo, useState } from "react"
import { Table } from "reactstrap"
import ActionButton from "Components/atoms/buttons/action-button"
import Chip from "Components/atoms/chip"
import SelectInput from "Components/molecule/inputs/select-input"
import CheckboxInput from "Components/molecule/inputs/checkbox-input"
import EmptyState from "Components/organisms/empty-state"
import LoadingState from "Components/organisms/loading-state"
import BillSplitModal from "./bill-split-modal"
import { BillAllocation, ExpenseBudget, ExpenseBudgetLine, VendorBill } from "models/expense-budget"
import { getExpenseBudgetVendorBills, saveExpenseBudgetBillLinks } from "slices/finance/expense-budget/thunk"
import { planWeights, splitByPlan, sumAmounts } from "utils/expense-budget"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatCurrency } from "utils/tools"

interface BillsSectionProps {
    budget: ExpenseBudget
    lines: ExpenseBudgetLine[]
}

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)
const NOT_LINKED = ""

const periodLabel = (fiscalPeriod: number) => calendar.fiscalToLabel(Math.floor(fiscalPeriod / 100), fiscalPeriod % 100)

// Auto-link: each unlinked bill line booked to the account of the vendor's products' cost code goes
// to those products (split by plan when several share it). Left pending for review until "Save links".
const autoLinks = (bills: VendorBill[], products: ExpenseBudgetLine[], budget: ExpenseBudget): Record<string, BillAllocation[]> => {
    const accountOf = (product: ExpenseBudgetLine) =>
        budget.costCodes.find(c => c.id === product.expenseBudgetCostCodeId)?.transactionAccount.code.trim()
    const links: Record<string, BillAllocation[]> = {}
    for (const bill of bills) {
        const account = bill.accountCode?.trim()
        if (bill.allocations.length > 0 || !account) continue
        const matches = products.filter(product => accountOf(product) === account)
        if (matches.length === 0) continue
        const split = splitByPlan(bill.amount, planWeights(matches, bill.fiscalPeriod))
        links[bill.id] = matches.map((product, i) => ({ expenseBudgetLineId: product.id, amount: split[i] }))
    }
    return links
}

// The vendor's Acumatica bills and adjustments (from all its Acumatica vendors) in the budget's
// fiscal year. Each bill line is linked to one product, or split across several with an amount
// each; linked amounts are the products' actual spend. Lines not counted yet (e.g. Pending
// Approval) can be linked too and start counting once released. Changes stay pending until
// "Save links".
const BillsSection = ({ budget, lines }: BillsSectionProps) => {
    const linkedVendors = budget.vendors.filter(vendor => vendor.codes.length > 0)
    const [vendorId, setVendorId] = useState(linkedVendors[0]?.id ?? "")
    const [bills, setBills] = useState<VendorBill[] | null>(null)
    const [pending, setPending] = useState<Record<string, BillAllocation[]>>({})
    const [splitting, setSplitting] = useState<VendorBill | null>(null)
    const [isOnlyUnlinked, setIsOnlyUnlinked] = useState(false)
    const [isOnlyCostCodeAccounts, setIsOnlyCostCodeAccounts] = useState(true)
    const [isSaving, setIsSaving] = useState(false)

    const loadBills = async () => {
        setPending({})
        if (!vendorId) return setBills([])
        setBills(null)
        const loaded = (await getExpenseBudgetVendorBills(budget.id, vendorId)) ?? []
        setBills(loaded)
        setPending(autoLinks(loaded, lines.filter(line => line.expenseBudgetVendorId === vendorId), budget))
    }

    useEffect(() => {
        loadBills()
    }, [vendorId, budget.id])

    // A vendor linked to Acumatica after the tab opened becomes the default.
    useEffect(() => {
        if (!vendorId && linkedVendors[0]) setVendorId(linkedVendors[0].id)
    }, [budget.vendors])

    const allocationsOf = (bill: VendorBill) => pending[bill.id] ?? bill.allocations
    const isLinked = (bill: VendorBill) => allocationsOf(bill).length > 0
    const setAllocations = (bill: VendorBill, allocations: BillAllocation[]) =>
        setPending(prev => ({ ...prev, [bill.id]: allocations }))

    const vendorProducts = useMemo(() => lines.filter(line => line.expenseBudgetVendorId === vendorId), [lines, vendorId])
    const productOptions = useMemo(() => [
        { value: NOT_LINKED, label: "— Not linked —" },
        ...vendorProducts.map(line => ({ value: line.id, label: line.productName })),
    ], [vendorProducts])
    const costCodeAccounts = useMemo(() => new Set(budget.costCodes.map(c => c.transactionAccount.code.trim())), [budget.costCodes])
    const hasSeveralCodes = (budget.vendors.find(v => v.id === vendorId)?.codes.length ?? 0) > 1

    const isInAccountFilter = (bill: VendorBill) =>
        !isOnlyCostCodeAccounts || (bill.accountCode !== null && costCodeAccounts.has(bill.accountCode.trim()))
    // Totals follow the account filter, so hidden lines (e.g. "Due from" accounts) don't count as not linked.
    const countedBills = (bills ?? []).filter(bill => bill.isCounted && isInAccountFilter(bill))
    const linkedTotal = sumAmounts(countedBills.filter(isLinked).map(bill => bill.amount))
    const unlinkedTotal = sumAmounts(countedBills.filter(bill => !isLinked(bill)).map(bill => bill.amount))
    const pendingCount = Object.keys(pending).length

    const visibleBills = (bills ?? []).filter(bill => (!isOnlyUnlinked || !isLinked(bill)) && isInAccountFilter(bill))

    // A single product takes the whole bill line amount.
    const handleSelectProduct = (bill: VendorBill, lineId: string) =>
        setAllocations(bill, lineId === NOT_LINKED ? [] : [{ expenseBudgetLineId: lineId, amount: bill.amount }])

    const handleSave = async () => {
        setIsSaving(true)
        const links = Object.entries(pending).map(([vendorBillLineId, allocations]) => ({ vendorBillLineId, allocations }))
        const isSaved = await saveExpenseBudgetBillLinks(budget.id, links)
        setIsSaving(false)
        if (!isSaved) return
        await loadBills()
    }

    const vendorOptions = linkedVendors.map(vendor => ({
        value: vendor.id,
        label: `${vendor.name} (${vendor.codes.map(code => code.vendorCode).join(", ")})`,
    }))

    const renderProductCell = (bill: VendorBill) => {
        const allocations = allocationsOf(bill)
        const productName = (lineId: string) => vendorProducts.find(line => line.id === lineId)?.productName ?? "Product"
        return (
            <div className="d-flex flex-column gap-1">
                {allocations.length > 1
                    ? <div className="d-flex align-items-center gap-2">
                        <span title={allocations.map(a => `${productName(a.expenseBudgetLineId)}: ${formatCurrency(a.amount)}`).join("\n")}>
                            Split · {allocations.length} products
                        </span>
                        <ActionButton onClick={() => setSplitting(bill)}>Edit split</ActionButton>
                    </div>
                    : <div className="d-flex align-items-center gap-2">
                        <SelectInput options={productOptions} value={allocations[0]?.expenseBudgetLineId ?? NOT_LINKED}
                            onChange={e => handleSelectProduct(bill, e.target.value)} />
                        <ActionButton disabled={vendorProducts.length < 2} onClick={() => setSplitting(bill)}>Split</ActionButton>
                    </div>}
                {!bill.isCounted && <Chip variant="secondary">Not counted yet · {bill.status}</Chip>}
            </div>
        )
    }

    const renderBills = () => {
        if (!vendorId) return <EmptyState title="No Acumatica vendors linked"
            subtitle="In the Budget tab, edit a vendor and choose its Acumatica vendors to see its bills here." />
        if (!bills) return <LoadingState title="Loading Bills" subtitle="Fetching the vendor's bills and adjustments..." />
        if (bills.length === 0) return <EmptyState title="No bills" subtitle={`No bills or adjustments for this vendor in FY${budget.fiscalYearId}.`} />

        return (
            <div style={{ overflowX: "auto" }}>
                <Table size="sm" hover className="align-middle">
                    <thead>
                        <tr>
                            <th>Date</th><th>Period</th>{hasSeveralCodes && <th>Acumatica</th>}<th>Type</th><th>Ref #</th><th>Vendor ref</th>
                            <th>Description</th><th>Account</th><th>Branch</th><th className="text-end">Amount</th><th>Product</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleBills.map(bill => (
                            <tr key={bill.id} className={bill.isCounted ? undefined : "text-muted"}
                                style={pending[bill.id] !== undefined ? { background: "var(--bs-warning-bg-subtle)" } : undefined}>
                                <td className="text-nowrap">{bill.docDate.slice(0, 10)}</td>
                                <td className="text-nowrap">{periodLabel(bill.fiscalPeriod)}</td>
                                {hasSeveralCodes && <td className="text-nowrap">{bill.vendorCode}</td>}
                                <td className="text-nowrap">{bill.docType}</td>
                                <td>{bill.refNbr}</td>
                                <td>{bill.vendorRef ?? "-"}</td>
                                <td>{bill.lineDescription ?? bill.docDescription ?? "-"}</td>
                                <td>{bill.accountCode ?? "-"}</td>
                                <td>{bill.branchCode ?? "-"}</td>
                                <td className="text-end text-nowrap">{formatCurrency(bill.amount)}</td>
                                <td style={{ minWidth: 300 }}>{renderProductCell(bill)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        )
    }

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2">
                <div>
                    <h5 className="mb-1">Bills & Adjustments (Acumatica)</h5>
                    {bills && bills.length > 0 &&
                        <span className={unlinkedTotal ? "text-danger" : "text-success"}>
                            Linked {formatCurrency(linkedTotal)} · Not linked {formatCurrency(unlinkedTotal)}
                        </span>}
                </div>
                <div className="d-flex flex-wrap align-items-end gap-2">
                    {vendorOptions.length > 0 &&
                        <SelectInput label="Vendor" options={vendorOptions} value={vendorId} onChange={e => setVendorId(e.target.value)} />}
                    <CheckboxInput label="Only not linked" checked={isOnlyUnlinked} onChange={e => setIsOnlyUnlinked(e.target.checked)} />
                    <CheckboxInput label="Only cost code accounts" checked={isOnlyCostCodeAccounts}
                        onChange={e => setIsOnlyCostCodeAccounts(e.target.checked)} />
                    <ActionButton disabled={pendingCount === 0 || isSaving} onClick={handleSave}>
                        {isSaving ? "Saving..." : `Save links${pendingCount ? ` (${pendingCount})` : ""}`}
                    </ActionButton>
                </div>
            </div>
            {renderBills()}
            <BillSplitModal bill={splitting} products={vendorProducts} allocations={splitting ? allocationsOf(splitting) : []}
                onClose={() => setSplitting(null)}
                onApply={allocations => {
                    if (splitting) setAllocations(splitting, allocations)
                    setSplitting(null)
                }} />
        </div>
    )
}

export default BillsSection
